import IntercomCampaignSync from "@/components/Campaign/IntercomCampaignSync";
import "@/dayjsConfig";
import i18n from "@/i18n";
import "@/index.css";
import "@/languageConfig";
import clsxm from "@/lib/clsxm";
import AgencyEditMenu from "@/pages/profile/AgencyEditMenu";
import CompanyEditMenu from "@/pages/profile/CompanyEditMenu";
import {PermissionProvider} from "@/permissions";
import SharedPostHogProvider from "@/posthog";
import {queryClient} from "@/queryClient";
import Layout from "@/routing/Layout";
import RedirectIfLoggedIn from "@/routing/RedirectIfLoggedIn";
import RedirectIfNotAgency from "@/routing/RedirectIfNotAgency";
import RedirectIfNotCompany from "@/routing/RedirectIfNotCompany";
import RequireAdmin from "@/routing/RequireAdmin";
import RequireAuth from "@/routing/RequireAuth";
import RequirePlan from "@/routing/RequirePlan";
import {selectedCompanyIdAtom} from "@/store/jotaiAtoms";
import {ModalProvider} from "@/store/modalContext";
import useTokenStore from "@/store/token";
import {fetchCampaign, fetchCompany, fetchUser} from "@/utils/loaderUtils";
import * as Sentry from "@sentry/react";
import {Elements} from "@stripe/react-stripe-js";
import {loadStripe} from "@stripe/stripe-js";
import "@tanstack/react-query";
import {QueryClientProvider} from "@tanstack/react-query";
import {ReactQueryDevtools} from "@tanstack/react-query-devtools";
import {getDefaultStore} from "jotai";
import {camelCase} from "lodash";
import "rc-slider/assets/index.css";
import {lazy} from "react";
import {createRoot} from "react-dom/client";
import {HelmetProvider} from "react-helmet-async";
import {I18nextProvider} from "react-i18next";
import {createBrowserRouter, Navigate, Outlet, replace} from "react-router";
import {RouterProvider} from "react-router/dom";
import {ToastContainer} from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "react-tooltip/dist/react-tooltip.css";
import {IntercomProvider} from "react-use-intercom";

const CustomerList = lazy(() => import("@/pages/agencies/CustomerListPage"));
const CombineAccounts = lazy(() => import("@/pages/agencies/CombineAccountsPage"));
const CreateAgency = lazy(() => import("@/pages/agencies/CreateAgencyPage"));
const RegisterAgency = lazy(() => import("@/pages/agencies/RegisterAgencyPage"));
const Customers = lazy(() => import("@/components/MultiAccount/Customers"));
const AgencyDashboardPage = lazy(() => import("@/pages/agencies/AgencyDashboardPage"));
const AgencyEdit = lazy(() => import("@/pages/profile/AgencyEdit"));
const BulkNotifications = lazy(() => import("@/components/Account/BulkNotifications"));
const BulkSms = lazy(() => import("@/components/Account/BulkSms"));
const InstagramMediaManager = lazy(() => import("@/components/Account/InstagramMediaManager"));
const TikTokMediaManager = lazy(() => import("@/components/Account/TikTokMediaManager"));
const LoginPage = lazy(() => import("@/pages/login/LoginPage"));
const RegistrationPage = lazy(() => import("@/pages/registerCompany/RegistrationPage"));
const VerifyEmailPage = lazy(() => import("@/pages/verifyEmail/VerifyEmailPage"));
const RequestPasswordResetPage = lazy(() => import("@/pages/requestPasswordReset/RequestPasswordResetPage"));
const Plans = lazy(() => import("@/pages/plans/Plans"));
const WelcomePage = lazy(() => import("@/pages/welcome/WelcomePage"));
const ImageUploadPage = lazy(() => import("@/pages/imageUpload/ImageUploadPage"));
const ResetPasswordPage = lazy(() => import("@/pages/resetPassword/ResetPasswordPage"));
const CampaignDetail = lazy(() => import("@/components/Campaign/CampaignDetail"));
const CampaignList = lazy(() => import("@/pages/campaigns/CampaignsPage"));
const CampaignFormPage = lazy(() => import("@/pages/campaignForm/CampaignFormPage"));
const ChatPage = lazy(() => import("@/pages/chat/ChatPage"));
const NotFound = lazy(() => import("@/components/Common/NotFound"));
const CompanyDetail = lazy(() => import("@/components/Company/CompanyDetail"));
const Invoices = lazy(() => import("@/pages/invoices/Invoices"));
const InvoicesAdmin = lazy(() => import("@/pages/invoices/InvoicesAdmin"));
const CompanyEditDetails = lazy(() => import("@/pages/profile/CompanyEditDetails"));
const InfluencerProfile = lazy(() => import("@/components/Influencer/InfluencerDetail/InfluencerProfile"));
const InfluencerEdit = lazy(() => import("@/components/Influencer/InfluencerEdit"));
const InfluencerNetwork = lazy(() => import("@/components/Influencer/influencerNetwork/InfluencerNetwork"));
const CampaignManagementRedirect = lazy(() => import("@/utils/CampaignManagementRedirect"));
const VerifyAccount = lazy(() => import("@/pages/verifyAccount/VerifyAccountPage"));
const CompanyDashboardPage = lazy(() => import("@/pages/companyDashboard/CompanyDashboardPage"));
const GroupPage = lazy(() => import("@/pages/groups/GroupPage"));

Sentry.init({
  dsn: import.meta.env.VITE_APP_ENV_IS_PRODUCTION === "true" ? import.meta.env.VITE_APP_SENTRY_DSN_PROD : import.meta.env.VITE_APP_SENTRY_DSN_DEV,
  integrations: [Sentry.browserTracingIntegration()],
  tracesSampleRate: 1.0,
  enabled: process.env.NODE_ENV === "production",
  replaysOnErrorSampleRate: 1.0,
});

const stripePromise = loadStripe(import.meta.env.VITE_APP_STRIPE_PUBLISHABLE_KEY);

const router = createBrowserRouter([
  {
    path: "/",
    element: <Layout />,
    errorElement: <Navigate to={"/login"} state={{from: location}} />,
    children: [
      {path: "/packages/", element: <Navigate to="/plans/" replace />},
      {
        element: <RedirectIfLoggedIn />,
        children: [
          {path: "/login/", element: <LoginPage />},
          {path: "/register/", element: <RegistrationPage />},
          {path: "/register/agency/", element: <RegisterAgency />},
          {path: "/verify-email/", element: <VerifyEmailPage />},
          {path: "/reset-password/", element: <ResetPasswordPage />},
          {path: "/request-reset/", element: <RequestPasswordResetPage />},
        ],
      },
      {path: "/welcome/", element: <WelcomePage />},
      {
        path: "/",
        element: <RequireAuth />,
        id: "root",
        children: [
          {
            path: "agency",
            element: <RedirectIfNotAgency />,
            id: "agency_root",
            loader: async () => {
              const userId = useTokenStore.getState().userId;

              if (!userId) {
                return replace("/login/");
              }

              const user = await fetchUser(userId);
              const isAgency = user.user_access_level === "AGENCY" || user.agencies?.[0]?.agency.multiple_brands;
              const isAdmin = user.user_type === "admin";
              const companyId = user.company?.id;

              return {isAgency, isAdmin, companyId};
            },
            children: [
              {
                path: ":agencyId/",
                loader: async () => {
                  getDefaultStore().set(selectedCompanyIdAtom, null);
                },
                children: [
                  {
                    path: "",
                    loader: ({params}) => replace(`/agency/${params.agencyId}/customers/`),
                  },
                  {path: "customers/", element: <Customers />},
                  {
                    path: "chat/",
                    element: <ChatPage />,
                    children: [{path: ":companyId/"}, {path: ":companyId/:campaignId/"}, {path: ":companyId/:campaignId/:influencerUserId/"}],
                  },
                  {
                    path: "edit/",
                    element: <AgencyEditMenu />,
                    children: [{path: "", element: <AgencyEdit />}],
                  },
                  {
                    path: "dashboard/",
                    element: <AgencyDashboardPage />,
                  },
                ],
              },
              {
                path: ":agencyId/subbrand/:companyId",
                element: <Outlet />,
                loader: async ({params}) => {
                  getDefaultStore().set(selectedCompanyIdAtom, Number(params.companyId));
                },
                children: [
                  {
                    path: "",
                    element: <CompanyDetail />,
                  },
                  {
                    path: "dashboard/",
                    element: <CompanyDashboardPage />,
                  },
                  {
                    path: "edit/",
                    element: <AgencyEditMenu />,
                    children: [{path: "", element: <CompanyEditDetails />}],
                  },
                ],
              },
            ],
          },
          {
            path: "company",
            element: <RedirectIfNotCompany />,
            id: "company_root",
            loader: async () => {
              const userId = useTokenStore.getState().userId;

              if (!userId) {
                return replace("/login/");
              }

              const user = await fetchUser(userId);
              const isComapny = user.user_access_level === "SINGLE_BRAND" || user.user_access_level === "SUB_BRAND";
              const isAdmin = user.user_type === "admin";
              const agencyId = user.agencies?.at(0)?.agency?.id;

              return {isComapny, isAdmin, agencyId};
            },
            children: [
              {
                path: ":companyId/",
                element: <CompanyDetail />,
                loader: async ({params}) => {
                  try {
                    const userId = useTokenStore.getState().userId;

                    if (!userId) {
                      return replace("/login/");
                    }

                    const user = await fetchUser(userId);
                    const isAgency = user.user_access_level === "AGENCY" || user.agencies?.[0]?.agency.multiple_brands;

                    if (!isAgency && user.user_access_level !== "INFLUENCER_ADMIN" && user.user_access_level !== "COMPANY_ADMIN") {
                      return;
                    }

                    const company = await fetchCompany(Number(params.companyId));

                    if (company.agency?.multiple_brands) {
                      return replace(`/agency/${company.agency?.id}/subbrand/${company.id}/`);
                    }
                  } catch (error) {
                    console.log("Error loading company:", error);
                  } finally {
                    return;
                  }
                },
              },
              {
                path: ":companyId/chat",
                element: <ChatPage />,
                children: [{path: ":campaignId/"}, {path: ":campaignId/:influencerUserId/"}],
              },
              {
                path: ":companyId/dashboard/",
                element: <CompanyDashboardPage />,
                loader: async ({params}) => {
                  const userId = useTokenStore.getState().userId;

                  if (!userId) {
                    return replace("/login/");
                  }

                  const user = await fetchUser(userId);
                  const isAgency = user.user_access_level === "AGENCY" || user.agencies?.[0]?.agency.multiple_brands;

                  if (!isAgency && user.user_access_level !== "INFLUENCER_ADMIN" && user.user_access_level !== "COMPANY_ADMIN") {
                    return;
                  }

                  const company = await fetchCompany(Number(params.companyId));

                  if (company.agency?.multiple_brands) {
                    return replace(`/agency/${company.agency?.id}/subbrand/${company.id}/dashboard/`);
                  }

                  return;
                },
              },
              {
                path: ":companyId/edit/",
                element: <CompanyEditMenu />,
                children: [{path: "", element: <CompanyEditDetails />}],
                loader: async ({params}) => {
                  try {
                    const userId = useTokenStore.getState().userId;

                    if (!userId) {
                      return replace("/login/");
                    }

                    const user = await fetchUser(userId);
                    const isAgency = user.user_access_level === "AGENCY" || user.agencies?.[0]?.agency.multiple_brands;

                    if (!isAgency && user.user_access_level !== "INFLUENCER_ADMIN" && user.user_access_level !== "COMPANY_ADMIN") {
                      return;
                    }

                    const company = await fetchCompany(Number(params.companyId));

                    if (company.agency?.multiple_brands) {
                      return replace(`/agency/${company.agency?.id}/subbrand/${company.id}/edit/`);
                    }
                  } catch (error) {
                    console.log("Error loading company:", error);
                  } finally {
                    return;
                  }
                },
              },
              {
                path: ":companyId/invoices/",
                element: <InvoicesAdmin />,
              },
            ],
          },
          {
            path: "admins",
            element: <RequireAdmin />,
            children: [
              {path: "customers/", element: <CustomerList />},
              {path: "accounts/combine/", element: <CombineAccounts />},
              {path: "agency/create/", element: <CreateAgency />},
              {path: "bulk-notification/", element: <BulkNotifications />},
              {path: "bulk-sms/", element: <BulkSms />},
              {
                path: "instagram-media-manager/",
                element: <InstagramMediaManager />,
              },
              {path: "tiktok-media-manager/", element: <TikTokMediaManager />},
            ],
          },
          {
            path: "/campaign-management/:campaignId/",
            element: <CampaignManagementRedirect />,
          },
          {
            path: "/campaign-management-ongoing/:campaignId/",
            element: <CampaignManagementRedirect />,
          },
          {
            path: "/campaign-reporting/:campaignId/",
            loader: ({params}) => replace(`/campaign/${params.campaignId}/?tab=reporting`),
          },
          {
            path: "/influencer/:influencerIdEdit/edit/",
            element: <InfluencerEdit />,
          },

          {path: "/invoices", element: <Invoices />},
          {path: "/group/:groupId", element: <GroupPage />},
          {path: "/campaigns/", element: <CampaignList />},
        ],
      },
      {
        path: "/",
        children: [
          {
            path: "/campaign/:campaignId/",
            loader: async ({params}) => {
              try {
                const userId = useTokenStore.getState().userId;
                const campaign = await fetchCampaign(Number(params.campaignId));

                if (!userId) {
                  return {campaign};
                }

                const user = await fetchUser(userId);
                const companyId = campaign.company_profile?.id;
                const isAgency = user.agencies?.length! > 0 && user.agencies?.[0]?.agency.multiple_brands;
                const hasAccessToSubBrand = user.sub_brands?.find(subBrand => subBrand.company.id === companyId);

                if (isAgency && hasAccessToSubBrand && !!companyId) {
                  getDefaultStore().set(selectedCompanyIdAtom, companyId);
                }

                return {campaign};
              } catch (error) {
                console.log("Error loading campaign:", error);
                return;
              }
            },
            children: [
              {
                path: "",
                element: (
                  <IntercomCampaignSync>
                    <CampaignDetail />
                  </IntercomCampaignSync>
                ),
              },
              {path: "edit/", element: <RequirePlan />, children: [{path: "", element: <CampaignFormPage />}]},
            ],
          },
        ],
      },
      {
        path: "/",
        element: <RequireAuth redirectTo="/register/" />,
        children: [
          {
            path: "/plans/",
            element: <Plans />,
          },
        ],
      },
      {path: "/image-upload/", element: <ImageUploadPage />},
      {path: "/influencer/:influencerId/", element: <InfluencerProfile />},
      {path: "/influencers-list/", element: <InfluencerNetwork />},
      {path: "/verify-account/", element: <VerifyAccount />},

      // --- Start of old routes that will redirect to the new, hierarchical routes ---
      {
        path: "/my_campaigns/:companyId/",
        loader: ({params}) => replace(`/company/${params.companyId}/dashboard/`),
      },
      {
        path: "/campaign-edit/:campaignId/",
        loader: ({params, request}) => replace(`/campaign/${params.campaignId}/edit/${new URL(request.url).search}`),
      },
      {
        path: "/company-edit/:companyId/",
        loader: ({params, request}) => replace(`/company/${params.companyId}/edit/${new URL(request.url).search}`),
      },
      {
        path: "/influencer-edit/:influencerIdEdit/",
        loader: ({params}) => replace(`/influencer/${params.influencerIdEdit}/edit/`),
      },
      {path: "/bulk-sms/", loader: () => replace("/admins/bulk-sms/")},
      {
        path: "/bulk-notification/",
        loader: () => replace("/admins/bulk-notification/"),
      },
      {
        path: "/instagram-media-manager/",
        loader: () => replace("/admins/instagram-media-manager/"),
      },
      {
        path: "/tiktok-media-manager/",
        loader: () => replace("/admins/tiktok-media-manager/"),
      },
      {
        path: "/chat/:campaignId/:influencerUserId/",
        loader: async ({params}) => {
          const userId = useTokenStore.getState().userId;

          if (!userId) {
            return replace("/login/");
          }

          const user = await fetchUser(userId);
          const isAgency = user.agencies?.length! > 0 && user.agencies?.[0]?.agency.multiple_brands;

          if (isAgency) {
            return replace(`/agency/${user.agencies?.at(0)?.agency?.id}/dashboard/`);
          }

          return replace(`/company/${user.company?.id}/chat/${params.campaignId}/${params.influencerUserId}/`);
        },
      },
      {
        path: "/chat/",
        loader: async ({request}) => {
          const parsed = new URLSearchParams(new URL(request.url).search);
          const camelCaseParams: Record<string, string> = {};

          parsed.forEach((value, key) => {
            camelCaseParams[camelCase(key)] = value;
          });

          const userId = useTokenStore.getState().userId;

          if (!userId) {
            return replace("/login/");
          }

          const user = await fetchUser(userId);
          const isAgency = user.agencies?.length! > 0 && user.agencies?.[0]?.agency.multiple_brands;

          if (isAgency) {
            return replace(`/agency/${user.agencies?.at(0)?.agency?.id}/chat/${camelCaseParams.companyId}/${camelCaseParams.campaignId}/${camelCaseParams.influencerUserId}/`);
          }

          return replace(`/company/${user.company?.id}/chat/${camelCaseParams.campaignId}/${camelCaseParams.influencerUserId}/`);
        },
      },
      // --- End of old routes ---

      {path: "*", element: <NotFound />},
    ],
  },
]);

createRoot(document.getElementById("root") as HTMLElement).render(
  <I18nextProvider i18n={i18n}>
    <QueryClientProvider client={queryClient}>
      <ReactQueryDevtools initialIsOpen={true} buttonPosition="bottom-left" position="right" />
      <ToastContainer
        icon={false}
        position="bottom-center"
        autoClose={5000}
        hideProgressBar
        closeOnClick
        pauseOnHover={false}
        className="z-99999 md:w-7/12"
        draggable
        closeButton={false}
        toastClassName={context => clsxm("rounded-lg border p-3.5 m-2 w-full", context?.type === "error" ? "bg-danger-light-red text-danger-medium-red border-danger-medium-red" : "border-success-medium-green bg-success-light-green text-success-medium-green")}
      />
      <SharedPostHogProvider enabled={import.meta.env.VITE_APP_ENV_IS_PRODUCTION === "true"}>
        <HelmetProvider>
          <Elements stripe={stripePromise}>
            <PermissionProvider>
              <IntercomProvider appId={import.meta.env.VITE_INTERCOM_APP_ID}>
                <ModalProvider>
                  <RouterProvider router={router} />
                </ModalProvider>
              </IntercomProvider>
            </PermissionProvider>
          </Elements>
        </HelmetProvider>
      </SharedPostHogProvider>
    </QueryClientProvider>
  </I18nextProvider>,
);
